import { queryOptions } from "@tanstack/react-query";
import { apiFetch } from "@/shared/api";

// Only modern remote MCP transport is supported. Stdio and the legacy
// HTTP+SSE transport were intentionally dropped — see backend/connectors.py.

export interface Connector {
  id: string;
  name: string;
  label: string;
  description: string;
  config: Record<string, unknown>;
  enabled: boolean;
  secret_keys: string[];
  created_at?: string | null;
  updated_at?: string | null;
  /** Scope of the row. Present in cross-space (`scope=all`) responses;
   *  `null` means the connector is in the caller's personal scope. */
  space?: { id: string; name: string } | null;
  /** Raw scope id, returned by single-row GET / PUT. `null` = personal. */
  space_id?: string | null;
}

export interface ConnectorBody {
  name: string;
  label: string;
  description: string;
  config: Record<string, unknown>;
  enabled: boolean;
  secrets: Record<string, string | null>;
  /** Target tenancy. `null` = personal scope; `"<uuid>"` = pinned to that
   *  team space. Sent on every save — backend distinguishes from "field
   *  omitted" by a sentinel, but the new UI is always explicit. */
  space_id?: string | null;
}

export interface ConnectorTool {
  name: string;
  /** Unprefixed MCP tool name — stable across connector renames, used as the
   *  key in `config.approval_required_tools`. Falls back to `name` for older
   *  backends that didn't include the field. */
  raw_name?: string;
  /** Human-readable label from MCP `Tool.title` (spec 2025-06+). May be null
   *  for older MCP servers that only set `name`/`description`. */
  title: string | null;
  description: string;
}

export interface ConnectorTestResult {
  ok: boolean;
  tools?: ConnectorTool[];
  error?: string;
}

type Scope = "personal" | "space" | "all";

export const connectorsQueryOptions = (scope: Scope = "all") =>
  queryOptions({
    queryKey: ["connectors", scope],
    queryFn: async (): Promise<Connector[]> => {
      const resp = await apiFetch(
        `/api/connectors?scope=${scope}`,
        undefined,
        scope === "all" ? { crossSpace: true } : undefined,
      );
      const data: { connectors: Connector[] } = await resp.json();
      return data.connectors ?? [];
    },
    staleTime: 30_000,
  });

export async function copyConnector(
  id: string,
  toSpaceId: string | null,
): Promise<Connector> {
  const resp = await apiFetch(`/api/connectors/${id}/copy`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ to_space_id: toSpaceId }),
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || "Ошибка копирования");
  }
  return resp.json();
}

export const connectorDetailQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ["connectors", id],
    queryFn: async (): Promise<Connector> => {
      const resp = await apiFetch(`/api/connectors/${id}`);
      return resp.json();
    },
    staleTime: 30_000,
  });

// Probes the MCP server and returns its tool list. Cached per connector id;
// the editor uses it to render the tool list inline. `staleTime: 5min` so
// reopening the editor doesn't re-spawn an MCP session every time.
export const connectorToolsQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ["connectors", id, "tools"],
    queryFn: () => testConnector(id),
    staleTime: 5 * 60_000,
  });

export async function createConnector(body: ConnectorBody): Promise<Connector> {
  const resp = await apiFetch("/api/connectors", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || "Ошибка создания коннектора");
  }
  return resp.json();
}

export async function updateConnector(id: string, body: ConnectorBody): Promise<Connector> {
  const resp = await apiFetch(`/api/connectors/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || "Ошибка сохранения");
  }
  return resp.json();
}

export async function deleteConnector(id: string): Promise<void> {
  await apiFetch(`/api/connectors/${id}`, { method: "DELETE" });
}

export async function testConnector(id: string): Promise<ConnectorTestResult> {
  const resp = await apiFetch(`/api/connectors/${id}/test`, { method: "POST" });
  return resp.json();
}

export function connectorLabel(c: Pick<Connector, "name" | "label">): string {
  return c.label || c.name;
}

// ── Per-session connector toggle ─────────────────────────────────────

export type SessionConnectorStatus = "active" | "failed" | "disabled";

export interface SessionConnectorItem {
  id: string;
  name: string;
  label: string;
  url: string;
  status: SessionConnectorStatus;
  error: string | null;
  /** Tools discovered when the MCP client started for this session. Empty
   *  until the connector has actually been activated (so empty on a fresh
   *  hero before the first run, or for failed connectors). */
  tools: ConnectorTool[];
}

export interface SessionConnectorsResponse {
  connectors: SessionConnectorItem[];
  defaults: string[];      // ids the agent had at session creation
  is_overridden: boolean;  // true if user set a per-session override
}

export const sessionConnectorsQueryOptions = (sessionId: string | null) =>
  queryOptions({
    queryKey: ["sessions", sessionId, "connectors"],
    queryFn: async (): Promise<SessionConnectorsResponse> => {
      const resp = await apiFetch(`/api/sessions/${sessionId}/connectors`);
      if (!resp.ok) throw new Error(await resp.text());
      return resp.json();
    },
    enabled: !!sessionId,
    staleTime: 30_000,
  });

export async function setSessionConnectors(
  sessionId: string,
  connectorIds: string[],
): Promise<void> {
  const resp = await apiFetch(`/api/sessions/${sessionId}/connectors`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ connector_ids: connectorIds }),
  });
  if (!resp.ok) throw new Error(await resp.text());
}
